/**
 * OrejaPanelBody - Phase-switching body content for the Oreja panel.
 *
 * Renders the appropriate phase component (recording, progress, clarification,
 * review, refinement, commit summary) based on the active session phase.
 *
 * @module OrejaPanelBody
 */

import { useState } from 'react';
import { Folder, ChevronDown } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useOrejaSession, type OrejaInput } from '@/hooks/useOrejaSession';
import { OrejaRecordingPhase, type OrejaProjectOption } from './OrejaRecordingPhase';
import { OrejaClarificationPhase } from './OrejaClarificationPhase';
import { OrejaReviewPhase } from './OrejaReviewPhase';
import { OrejaProgressStepper } from './OrejaProgressStepper';
import { OrejaCommitSummary } from './OrejaCommitSummary';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrejaPanelBodyProps {
  /** Current session phase. */
  phase: string;
  /** The session hook instance. */
  sessionHook: ReturnType<typeof useOrejaSession>;
  /** Available project options. */
  projects: OrejaProjectOption[];
  /** Currently selected project. */
  selectedProject: OrejaProjectOption | null;
  /** Callback when project is selected. */
  onProjectSelect: (clientId: string, projectId: string, listId: string) => void;
  /** Callback to send initial recording/text. */
  onSend: (input: OrejaInput) => void;
  /** Callback to send clarification answers (with optional radio context for voice). */
  onSendAnswers: (input: OrejaInput, radioContext?: string) => void;
  /** Callback to request proposal refinement. */
  onRefine: () => void;
  /** Callback to commit selected proposals. */
  onCommit: (selectedUpdated: number[], selectedNew: number[]) => void;
  /** Callback to start a new session. */
  onStartNew: () => void;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Collapsed summary of proposals shown during refine phase. */
function RefineSummary({ proposals }: { proposals: import('@/lib/oreja/types').OrejaProposals }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-4 mt-3">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left"
        >
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          Current proposals ({proposals.updated_tasks.length} updates, {proposals.new_tasks.length} new)
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 rounded-md bg-muted/50 p-2.5 space-y-1 text-xs text-muted-foreground">
          {proposals.updated_tasks.map((t, i) => (
            <p key={`u-${i}`}>• Update: {t.task_title}</p>
          ))}
          {proposals.new_tasks.map((t, i) => (
            <p key={`n-${i}`}>
              • {t.parent_task_id ? 'New Subtask' : 'New'}: {t.title}
              {t.parent_task_id && (
                <span className="text-muted-foreground/70"> (under {t.parent_task_id})</span>
              )}
            </p>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the appropriate phase component based on the session phase.
 *
 * @param props - Body props with phase and callbacks
 * @returns The phase-appropriate component
 */
export function OrejaPanelBody({
  phase,
  sessionHook,
  projects,
  selectedProject,
  onProjectSelect,
  onSend,
  onSendAnswers,
  onRefine,
  onCommit,
  onStartNew,
}: OrejaPanelBodyProps) {
  const showBreadcrumb = phase !== 'recording' && selectedProject;

  const breadcrumb = showBreadcrumb ? (
    <div className="flex items-center gap-1.5 px-4 pt-3 pb-1 text-xs text-muted-foreground">
      <Folder className="h-3 w-3" />
      <span className="font-medium">{selectedProject.label}</span>
    </div>
  ) : null;

  switch (phase) {
    case 'recording':
      return (
        <OrejaRecordingPhase
          onSend={onSend}
          onProjectSelect={onProjectSelect}
          selectedProject={selectedProject}
          projects={projects}
          isLoading={sessionHook.isProcessing}
        />
      );

    case 'uploading':
    case 'transcribing':
    case 'analyzing':
      return (
        <>
          {breadcrumb}
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <OrejaProgressStepper phase={sessionHook.phase} />
            <p className="text-sm text-muted-foreground">Processing your update...</p>
          </div>
        </>
      );

    case 'answering':
    case 'proposing':
      return (
        <>
          {breadcrumb}
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <OrejaProgressStepper phase={sessionHook.phase} />
            <p className="text-sm text-muted-foreground">Generating task proposals...</p>
          </div>
        </>
      );

    case 'committing':
      return (
        <>
          {breadcrumb}
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <OrejaProgressStepper phase={sessionHook.phase} />
            <p className="text-sm text-muted-foreground">Pushing changes to ClickUp...</p>
          </div>
        </>
      );

    case 'clarifying':
      return (
        <>
          {breadcrumb}
          <OrejaClarificationPhase
            transcript={sessionHook.transcript ?? ''}
            questions={sessionHook.questions ?? []}
            onSendAnswers={onSendAnswers}
            onSkipClarification={sessionHook.skipClarification}
            isLoading={sessionHook.isProcessing}
          />
        </>
      );

    case 'reviewing':
      return sessionHook.proposals ? (
        <>
        {breadcrumb}
        <OrejaReviewPhase
          proposals={sessionHook.proposals}
          existingTasks={[]}
          clickupListId={sessionHook.clickupListId}
          onProposalEdit={(type, index, field, value) => {
            sessionHook.updateProposals((prev) => {
              // Convert empty parent_task_id to null (cleared by "Make standalone")
              const resolved = field === 'parent_task_id' && !value ? null : value;
              if (type === 'updated') {
                const updated = [...prev.updated_tasks];
                updated[index] = { ...updated[index], [field]: resolved };
                return { ...prev, updated_tasks: updated };
              }
              const newTasks = [...prev.new_tasks];
              newTasks[index] = { ...newTasks[index], [field]: resolved };
              return { ...prev, new_tasks: newTasks };
            });
          }}
          onEditFieldChange={(index, fieldChangeIndex, newToValue) => {
            sessionHook.updateProposals((prev) => {
              const updated = [...prev.updated_tasks];
              const changes = [...updated[index].field_changes];
              changes[fieldChangeIndex] = { ...changes[fieldChangeIndex], to: newToValue };
              updated[index] = { ...updated[index], field_changes: changes };
              return { ...prev, updated_tasks: updated };
            });
          }}
          onProposalToggle={() => {}}
          onRefine={onRefine}
          onCommit={onCommit}
          onAddTask={(task) => {
            sessionHook.updateProposals((prev) => ({
              ...prev,
              new_tasks: [...prev.new_tasks, task],
            }));
          }}
          isCommitting={sessionHook.isProcessing && phase === 'committing'}
          isRefining={sessionHook.isProcessing && phase === 'refining'}
        />
        </>
      ) : (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">Loading proposals...</p>
        </div>
      );

    case 'refining':
      return (
        <>
        {breadcrumb}
        {/* Collapsed summary of current proposals for context */}
        {sessionHook.proposals && (
          <RefineSummary proposals={sessionHook.proposals} />
        )}
        <OrejaRecordingPhase
          onSend={(input) => {
            void sessionHook.refine(input);
          }}
          onProjectSelect={onProjectSelect}
          selectedProject={selectedProject}
          projects={projects}
          isLoading={sessionHook.isProcessing}
          promptText="Add more detail or corrections to refine the proposals"
          projectLocked
        />
        </>
      );

    case 'committed':
      return (
        <>
        {breadcrumb}
        <OrejaCommitSummary
          committedChanges={sessionHook.session?.committed_changes ?? []}
          failedCommits={sessionHook.failedCommits}
          onRetryFailed={sessionHook.retryFailed}
          isRetrying={sessionHook.isProcessing}
          onStartNew={onStartNew}
        />
        </>
      );

    default:
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">Unknown phase: {phase}</p>
        </div>
      );
  }
}
